// apps/provider/src/stores/calendarAvailabilityStore.js
// Route-persistent provider availability + schedule overrides for the
// calendar grids. Survives AppointmentCalendar unmounts so returning from a
// patient chart paints working-hours shading immediately (no grey flash).
//
// Keying model:
// - locationKey (orgId|locationId): mismatch means the cached rows are for the
//   wrong location scope — clear and refetch in the foreground.
// - providersKey (sorted provider ids): a changed provider set with the SAME
//   location refetches the full set but merges WITHOUT clearing, so existing
//   columns keep their shading while new ones fill in.

import { create } from 'zustand';
import { apiService } from '../services/api';
import { mergeScheduleTemplates } from '../components/calendar/calendarUtils';

const REVALIDATE_MIN_AGE_MS = 60000;
// Realtime override-event debounce: events NEVER fetch directly — they mark
// providers dirty and one flush per window does bounded work (herd-safe)
const OVERRIDE_INGEST_DEBOUNCE_MS = 3000;
// Random spread on the flush (2026-09-22): every tab receives the same
// override event at the same instant, so a fixed debounce flushed from every
// tab in the same second (27/s on public/schedule-overrides).
const OVERRIDE_INGEST_JITTER_MAX_MS = 5000;
// Few dirty providers -> small per-provider fetches; more -> ONE full refresh
const OVERRIDE_PROVIDER_FETCH_MAX = 3;

// ---------- module-level bookkeeping (not reactive state) ----------

// in-flight fetch dedupe: key -> Promise
const inFlight = new Map();
// realtime override ingest: dirty provider ids + debounce timer
let dirtyProviders = new Set();
let overrideIngestTimer = null;

const initialState = {
  availabilityByProvider: {},
  overridesByProvider: {},
  locationKey: null,
  providersKey: null,
  fetchedAt: null,
  hasLoaded: false,
  isFetching: false,
  // Params of the most recent ensureAvailability call — used by refresh()
  lastParams: null,
};

const buildLocationKey = ({ organizationId, locationId }) =>
  `${organizationId}|${locationId || ''}`;

const buildProvidersKey = (providerIds = []) => [...providerIds].sort().join(',');

// Group availability rows per provider; union-merge schedule templates when a
// provider has rows at multiple locations (no location filter active).
// Ported from AppointmentCalendar's reloadAvailabilities.
function buildAvailabilityMap(availList) {
  const rowsByProvider = {};
  for (const avail of availList) {
    if (!avail?.provider_id) continue;
    if (!rowsByProvider[avail.provider_id]) rowsByProvider[avail.provider_id] = [];
    rowsByProvider[avail.provider_id].push(avail);
  }

  // Every entry carries `rows` (the underlying provider_availability rows) so
  // per-date consumers (ProviderDayColumn, the waitlist map) can resolve
  // scheduled changes and history per row via calendarUtils.resolveTemplateForRows.
  // The merged `schedule_template` below is kept for anything not yet converted.
  const availMap = {};
  for (const [providerId, rows] of Object.entries(rowsByProvider)) {
    if (rows.length === 1) {
      availMap[providerId] = { ...rows[0], rows };
      continue;
    }
    const merged = mergeScheduleTemplates(rows.map(r => r.schedule_template).filter(Boolean));
    // Preserve the schedule bounds through the merge: each applies only when
    // EVERY row carries one (an unbounded template anywhere means no bound);
    // the earliest start wins and the latest end wins.
    const efDates = rows.map(r => r.schedule_template?.effective_from).filter(Boolean);
    const euDates = rows.map(r => r.schedule_template?.effective_until).filter(Boolean);
    // rows[0]'s pending change / history must not leak onto the merged entry:
    // they belong to one location only and are resolved per row via `rows`.
    const { pending_schedule_template: _p, schedule_template_history: _h, ...first } = rows[0];
    availMap[providerId] = {
      ...first,
      rows,
      schedule_template: {
        ...merged,
        ...(efDates.length > 0 && efDates.length === rows.length
          ? { effective_from: [...efDates].sort()[0] }
          : {}),
        ...(euDates.length > 0 && euDates.length === rows.length
          ? { effective_until: [...euDates].sort().at(-1) }
          : {}),
      },
      is_active: rows.some(r => r.is_active),
    };
  }
  return availMap;
}

function buildOverridesMap(overrideList) {
  const overMap = {};
  for (const ov of overrideList) {
    if (ov && ov.provider_id) {
      if (!overMap[ov.provider_id]) overMap[ov.provider_id] = [];
      overMap[ov.provider_id].push(ov);
    }
  }
  return overMap;
}

export const useCalendarAvailabilityStore = create((set, get) => ({
  ...initialState,

  // params: { providerIds: string[], locationId, organizationId }
  // opts: { background, force } — cached data is served instantly; a fetch
  // younger than REVALIDATE_MIN_AGE_MS is skipped unless forced.
  ensureAvailability: async (params, { background = false, force = false } = {}) => {
    const { providerIds = [], locationId, organizationId } = params;
    if (!organizationId || providerIds.length === 0) return;

    const locationKey = buildLocationKey({ organizationId, locationId });
    const providersKey = buildProvidersKey(providerIds);
    const state = get();

    // Wrong location scope — cached rows would be misleading; clear + foreground
    if (state.locationKey !== null && state.locationKey !== locationKey) {
      set({
        availabilityByProvider: {},
        overridesByProvider: {},
        hasLoaded: false,
        fetchedAt: null,
      });
      background = false;
    } else if (
      state.locationKey === locationKey &&
      state.providersKey === providersKey &&
      state.fetchedAt &&
      Date.now() - state.fetchedAt < REVALIDATE_MIN_AGE_MS &&
      !force
    ) {
      return;
    }

    const flightKey = `${locationKey}|${providersKey}`;
    const existing = inFlight.get(flightKey);
    if (existing) {
      if (!force) return existing;
      // Forced refresh after a write: a fetch that started BEFORE the write
      // completed would resolve with stale data — wait it out, then refetch.
      await existing.catch(() => {});
    }

    const fetchStartedAt = Date.now();
    if (!background) set({ isFetching: true });
    set({ lastParams: params, locationKey, providersKey });

    const run = (async () => {
      try {
        const idsCsv = providerIds.join(',');
        const [allAvailability, allOverrides] = await Promise.all([
          apiService.getProviderAvailabilityPublic(idsCsv, {
            locationId: locationId || undefined,
            organizationId,
          }),
          apiService.getScheduleOverridesPublic(idsCsv, undefined, undefined, { organizationId }),
        ]);

        const availList = Array.isArray(allAvailability) ? allAvailability : [allAvailability];
        const overrideList = Array.isArray(allOverrides) ? allOverrides : [];

        const availMap = buildAvailabilityMap(availList);
        const overMap = buildOverridesMap(overrideList);

        set((current) => {
          // Merge without clearing: providers outside this fetch keep their
          // entries (incremental provider-set changes don't flash)
          const nextAvail = { ...current.availabilityByProvider, ...availMap };
          const nextOver = { ...current.overridesByProvider, ...overMap };

          // Skip the state update entirely when nothing changed — avoids
          // identity churn in downstream memos (waitlist matching) on no-op
          // background refreshes
          const unchanged =
            JSON.stringify(nextAvail) === JSON.stringify(current.availabilityByProvider) &&
            JSON.stringify(nextOver) === JSON.stringify(current.overridesByProvider);

          return {
            ...(unchanged ? {} : { availabilityByProvider: nextAvail, overridesByProvider: nextOver }),
            fetchedAt: fetchStartedAt,
            hasLoaded: true,
          };
        });
      } catch (error) {
        console.error('calendarAvailabilityStore: fetch failed:', error);
      } finally {
        inFlight.delete(flightKey);
        if (!background) set({ isFetching: false });
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // Forced background refresh of the last-requested provider set — the
  // mutation-handler path (time-off saves, override paste/delete, open-hours).
  refresh: () => {
    const params = get().lastParams;
    if (!params) return;
    return get().ensureAvailability(params, { background: true, force: true });
  },

  // ---------- realtime ingest (dirty-provider debounce, tiered flush) ----------

  // Queue provider ids from realtime override events (metadata-only payloads).
  // Debounced: a few dirty providers -> one small per-provider refetch each,
  // wholesale-replacing that provider's slice with canonical server state;
  // a burst spanning many providers -> ONE full refresh(). Events never fetch
  // directly — this bound is what keeps synchronized bursts herd-safe.
  ingestOverrideEvent: (event) => {
    const state = get();
    if (!state.hasLoaded || !state.lastParams) return;
    const visible = new Set(state.lastParams.providerIds || []);
    const eventIds = event?.provider_ids || (event?.provider_id != null ? [event.provider_id] : []);
    for (const id of eventIds) {
      if (visible.has(id)) dirtyProviders.add(id);
    }
    if (dirtyProviders.size === 0 || overrideIngestTimer) return;
    // Hidden tabs only mark dirty; flushOverrideQueue() runs on visible
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    get()._scheduleOverrideFlush();
  },

  // Tab became visible with dirty providers queued while hidden
  flushOverrideQueue: () => {
    if (dirtyProviders.size === 0 || overrideIngestTimer) return;
    get()._scheduleOverrideFlush();
  },

  _scheduleOverrideFlush: () => {
    overrideIngestTimer = setTimeout(async () => {
      overrideIngestTimer = null;
      // Hidden by the time the debounce fires: keep the providers dirty for
      // flushOverrideQueue() on visible
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const queued = [...dirtyProviders];
      dirtyProviders = new Set();

      if (queued.length > OVERRIDE_PROVIDER_FETCH_MAX) {
        get().refresh();
        return;
      }

      const organizationId = get().lastParams?.organizationId;
      if (!organizationId) return;
      const results = await Promise.all(
        queued.map((pid) =>
          apiService.getScheduleOverridesPublic(pid, undefined, undefined, { organizationId })
            .catch((err) => {
              // No retry — the next event, reconnect, or SWR refetch heals
              console.error('calendarAvailabilityStore: override ingest fetch failed:', err);
              return null;
            })
        )
      );
      set((current) => {
        const nextOver = { ...current.overridesByProvider };
        let changed = false;
        results.forEach((rows, i) => {
          if (rows === null) return;
          const pid = queued[i];
          const list = Array.isArray(rows) ? rows : [];
          // Replace the slice wholesale (including with []) — deletions must
          // not survive a merge. fetchedAt is untouched: it tracks full-set
          // freshness for the SWR skip, which this partial update isn't.
          if (JSON.stringify(nextOver[pid] || []) !== JSON.stringify(list)) {
            nextOver[pid] = list;
            changed = true;
          }
        });
        return changed ? { overridesByProvider: nextOver } : {};
      });
    }, OVERRIDE_INGEST_DEBOUNCE_MS + Math.random() * OVERRIDE_INGEST_JITTER_MAX_MS);
  },

  reset: () => {
    dirtyProviders = new Set();
    if (overrideIngestTimer) {
      clearTimeout(overrideIngestTimer);
      overrideIngestTimer = null;
    }
    set({ ...initialState });
  },
}));
